import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { OverrideModeProvider } from './contexts/OverrideModeContext';
import { BrandingProvider } from './branding';
import ProtectedRoute from './components/ProtectedRoute';
import Header from './components/Header';
import Footer from './components/Footer';
// Eager imports for landing + auth (every visitor lands here).
import Home from './pages/Home';
import ZepLandingPage from './pages/ZepLandingPage';
import Login from './pages/Login';
import Register from './pages/Register';
import InsuredAuth from './pages/InsuredAuth';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import PublicQuoteRequestPage from './pages/PublicQuoteRequestPage';
import NTOAPublicLanding from './pages/NTOAPublicLanding';
import GetAppointedGate from './pages/GetAppointedGate';

// Lazy-loaded: heavy pages (>500 lines or rarely-used) become their own
// chunks so the first paint only downloads what's needed for the current
// route. Cuts initial bundle ~40-60% — session 213.61.
const QuoteForm = lazy(() => import('./pages/QuoteForm'));
const QuoteResumeRedirect = lazy(() => import('./pages/QuoteResumeRedirect'));
const AgentDashboard = lazy(() => import('./pages/AgentDashboard'));
const UnderwriterDashboard = lazy(() => import('./pages/UnderwriterDashboard'));
const SuperAdminDashboard = lazy(() => import('./pages/SuperAdminDashboard'));
const AccountingDashboard = lazy(() => import('./pages/AccountingDashboard'));
const DeclarationsPage = lazy(() => import('./pages/DeclarationsPage'));
const PaymentReturn = lazy(() => import('./pages/PaymentReturn'));
const CompareCarriersPage = lazy(() => import('./pages/CompareCarriersPage'));
const SharedMVRResult = lazy(() => import('./pages/SharedMVRResult'));
const PlatformAdminPortal = lazy(() => import('./pages/PlatformAdminPortal'));
const PlatformLoginGate = lazy(() => import('./pages/PlatformLoginGate'));
const InsuredPortal = lazy(() => import('./pages/InsuredPortal'));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'));
const ClientDetailPopoutPage = lazy(() => import('./pages/ClientDetailPopoutPage'));
const W9UploadPage = lazy(() => import('./pages/W9UploadPage'));
const VaultPage = lazy(() => import('./pages/VaultPage'));
const TicketsPage = lazy(() => import('./pages/TicketsPage'));
const TicketDetailPage = lazy(() => import('./pages/TicketDetailPage'));
const AdminIPFSMappings = lazy(() => import('./pages/AdminIPFSMappings'));
const AdminAgencyProbation = lazy(() => import('./pages/AdminAgencyProbation'));
const AdminSystemHealth = lazy(() => import('./pages/AdminSystemHealth'));
const AdminAccountingDefaults = lazy(() => import('./pages/AdminAccountingDefaults'));
const AdminDocumentDisclaimers = lazy(() => import('./pages/AdminDocumentDisclaimers'));
const AdminSharedInboxes = lazy(() => import('./pages/AdminSharedInboxes'));
const AdminEmailIntake = lazy(() => import('./pages/AdminEmailIntake'));
const OAQuoteForm = lazy(() => import('./pages/OAQuoteForm'));
const OAQueue = lazy(() => import('./pages/OAQueue'));
const OAPolicyDetail = lazy(() => import('./pages/OAPolicyDetail'));
import { Toaster } from './components/ui/sonner';
import './App.css';

// Inline spinner shown while a lazy chunk is downloading. Intentionally
// minimal — anything heavier would defeat the purpose of code-splitting.
const RouteLoading = () => (
  <div className="min-h-[60vh] flex items-center justify-center" data-testid="route-loading">
    <div className="flex items-center gap-3 text-slate-500">
      <div className="w-5 h-5 border-2 border-slate-300 border-t-blue-600 rounded-full animate-spin" />
      <span className="text-sm">Loading…</span>
    </div>
  </div>
);


// Dashboard Router - redirects based on role
// Tenants other than "zion" (legacy default) don't yet have their data routed
// to their own DB — so every dashboard query would currently read ZUW's data.
// Until full data isolation is wired in the backend, these users get a holding
// page so no tenant ever sees another tenant's data. (Platform admins and the
// zion super admin are exempt.)
const DEFAULT_TENANT_ID = 'zion';

const DashboardRouter = () => {
  const { user, logout } = useAuth();

  // HARD GUARDRAIL — prevent cross-tenant data leak.
  // Any logged-in tenant user whose tenant_id is set AND is not the default
  // (zion) gets a setup-pending screen until backend-level tenant-DB routing
  // is complete. Platform admins bypass this and go straight to /platform.
  if (
    user.role !== 'platform_admin' &&
    user.tenant_id &&
    user.tenant_id !== DEFAULT_TENANT_ID
  ) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="max-w-lg w-full bg-white border border-slate-200 rounded-lg shadow-sm p-8">
          <div className="text-amber-500 mb-4">
            <svg className="h-12 w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h1 className="text-xl font-bold text-slate-900 mb-2">Your Tenant Workspace Is Being Provisioned</h1>
          <p className="text-sm text-slate-600 mb-4">
            You're signed in as <code className="bg-slate-100 px-1 rounded">{user.email}</code> for tenant
            &nbsp;<code className="bg-slate-100 px-1 rounded">{user.tenant_id}</code>.
          </p>
          <p className="text-sm text-slate-600 mb-4">
            Your workspace has been created with empty defaults (0 carriers, 0 rates, 0 agencies),
            and your credentials work. Tenant-scoped data routing is currently being finalized —
            once complete, logging in here will take you straight to your Super Admin dashboard for
            this tenant, fully isolated from every other tenant.
          </p>
          <p className="text-sm text-slate-600 mb-6">
            The platform admin will notify you when your workspace is live.
            Until then, no tenant can see another tenant's data.
          </p>
          <button
            onClick={async () => { await logout(); window.location.href = '/login'; }}
            className="w-full bg-slate-900 hover:bg-slate-800 text-white px-4 py-2 rounded font-semibold"
            data-testid="tenant-pending-signout"
          >
            Sign Out
          </button>
        </div>
      </div>
    );
  }

  if (user.role === 'agent' || user.role === 'producer') {
    return <AgentDashboard />;
  } else if (user.role === 'agent_admin') {
    // Agent Admin sees agency-wide dashboard (similar to agent but can add producers)
    return <AgentDashboard />;
  } else if (user.role === 'underwriter') {
    // Session 213.14 — UW gets full SuperAdminDashboard shell with role-gated
    // sidebar items hidden where the UW lacks privileges. Replaces the
    // legacy 2,562-line UnderwriterDashboard which lacked the left-nav UX.
    return <SuperAdminDashboard />;
  } else if (user.role === 'super_admin') {
    return <SuperAdminDashboard />;
  } else if (user.role === 'platform_admin') {
    // Platform admin's home is /platform, not /dashboard
    return <Navigate to="/platform" replace />;
  } else if (user.role === 'accounting') {
    return <AccountingDashboard />;
  }

  return <Navigate to="/login" />;
};

/**
 * Session 214.5 — Auth-aware /quote route.
 * Logged-in producers/agents/admins land directly on the in-app QuoteForm.
 * Everyone else (logged-out visitors, NTOA members, insureds) gets the
 * GetAppointedGate funnel so we don't expose the submission UI publicly.
 */
const QuoteOrGate = () => {
  const { user, loading } = useAuth();
  if (loading) return <RouteLoading />;
  const producerRoles = ['agent', 'producer', 'agent_admin', 'super_admin', 'underwriter'];
  if (user && producerRoles.includes(user.role)) {
    return <QuoteForm />;
  }
  return <GetAppointedGate />;
};

function App() {
  return (
    <div className="App">
      <BrandingProvider>
        <AuthProvider>
          <OverrideModeProvider>
          <Toaster richColors position="top-right" />
          <BrowserRouter>
          <Suspense fallback={<RouteLoading />}>
          <Routes>
            {/* Public routes */}
            {/* Session 215.81 — customer-facing home is the Zion
                Underwriting Partners wholesale landing. Users must
                go through /get-appointed → /login to reach the
                platform. The previous ZEP marketing page is still
                available at /platform-overview for internal use. */}
            <Route
              path="/"
              element={
                <>
                  <Header />
                  <main className="pt-[120px]">
                    <Home />
                  </main>
                  <Footer />
                </>
              }
            />
            {/* Kept for any bookmarks pointing at the old tenant home */}
            <Route path="/tenant-home" element={<Navigate to="/" replace />} />
            {/* Internal — the ZEP platform overview page (no longer
                the customer home). Not linked from public nav. */}
            <Route path="/platform-overview" element={<ZepLandingPage />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            {/* Public — no auth — legal pages required for production launch */}
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            {/* Public — no auth — insured-facing quote request */}
            <Route path="/get-a-quote" element={<PublicQuoteRequestPage />} />
            {/* Session 214.5 — Public CTA "Get a Quote" funnels through
                this gate. Authenticated agents/producers/admins skip the
                gate entirely and land on the in-app QuoteForm (see the
                /quote protected route further down). */}
            <Route path="/get-appointed" element={<GetAppointedGate />} />
            {/* Public — no auth — NTOA marketing landing (ntoassoc.com / .net) */}
            <Route path="/ntoa" element={<NTOAPublicLanding />} />
            <Route path="/w9-upload/:token" element={<W9UploadPage />} />
            <Route path="/vault/:token" element={<VaultPage />} />
            <Route
              path="/tickets"
              element={
                <ProtectedRoute allowedRoles={['super_admin','underwriter','accounting','agent','agent_admin','producer']}>
                  <><Header /><TicketsPage /><Footer /></>
                </ProtectedRoute>
              }
            />
            <Route
              path="/tickets/:ticketId"
              element={
                <ProtectedRoute allowedRoles={['super_admin','underwriter','accounting','agent','agent_admin','producer']}>
                  <><Header /><TicketDetailPage /><Footer /></>
                </ProtectedRoute>
              }
            />

            {/* Public shared MVR pre-screen result (token-gated, 7-day expiry) */}
            <Route path="/shared/mvr/:token" element={<SharedMVRResult />} />

            {/* Session 213.9 — IPFS premium-finance mappings admin */}
            <Route
              path="/admin/ipfs-mappings"
              element={
                <ProtectedRoute allowedRoles={['super_admin']}>
                  <><Header /><AdminIPFSMappings /><Footer /></>
                </ProtectedRoute>
              }
            />

            {/* Session 213.13 — Agency probation watchlist */}
            <Route
              path="/admin/agency-probation"
              element={
                <ProtectedRoute allowedRoles={['super_admin']}>
                  <><Header /><AdminAgencyProbation /><Footer /></>
                </ProtectedRoute>
              }
            />

            {/* Session 213.16 — Daily System Health monitor */}
            <Route
              path="/admin/system-health"
              element={
                <ProtectedRoute allowedRoles={['super_admin']}>
                  <><Header /><AdminSystemHealth /><Footer /></>
                </ProtectedRoute>
              }
            />

            {/* Session 213.17 — Accounting defaults (reinstatement fee, etc.) */}
            <Route
              path="/admin/accounting-defaults"
              element={
                <ProtectedRoute allowedRoles={['super_admin']}>
                  <><Header /><AdminAccountingDefaults /><Footer /></>
                </ProtectedRoute>
              }
            />

            {/* Session 213.41 — Printed document disclaimers (per doc type + state addenda) */}
            <Route
              path="/admin/document-disclaimers"
              element={
                <ProtectedRoute allowedRoles={['super_admin']}>
                  <><Header /><AdminDocumentDisclaimers /><Footer /></>
                </ProtectedRoute>
              }
            />

            {/* Session 213.20 — Shared Inbox Groups (SIG) configuration */}
            <Route
              path="/admin/shared-inboxes"
              element={
                <ProtectedRoute allowedRoles={['super_admin']}>
                  <><Header /><AdminSharedInboxes /><Footer /></>
                </ProtectedRoute>
              }
            />

            {/* Session 213.20 — Inbound Email Intake queue (SIG-routed) */}
            <Route
              path="/admin/email-intake"
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'underwriter', 'accounting']}>
                  <><Header /><AdminEmailIntake /><Footer /></>
                </ProtectedRoute>
              }
            />

            {/* Dedicated Platform Admin portal (Zion Enterprises landlord) */}
            <Route path="/platform" element={<PlatformLoginGate />} />
            <Route path="/platform/login" element={<PlatformLoginGate />} />

            {/* Insured-Facing Customer Portal */}
            <Route path="/insured/login" element={<InsuredAuth mode="login" />} />
            <Route path="/insured/register" element={<InsuredAuth mode="register" />} />
            <Route path="/insured/forgot-password" element={<InsuredAuth mode="forgot-password" />} />
            <Route path="/insured/reset-password" element={<InsuredAuth mode="reset-password" />} />
            <Route path="/insured/accept-invite" element={<InsuredAuth mode="accept-invite" />} />
            <Route path="/insured/magic-link" element={<InsuredAuth mode="magic-link" />} />
            <Route path="/insured" element={<InsuredPortal />} />

            {/* Protected routes */}
            <Route
              path="/client/:client_id"
              element={
                <ProtectedRoute>
                  <ClientDetailPopoutPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <DashboardRouter />
                </ProtectedRoute>
              }
            />
            <Route
              path="/quote"
              element={<QuoteOrGate />}
            />
            <Route
              path="/quote/oa"
              element={
                <ProtectedRoute allowedRoles={['agent', 'producer', 'agent_admin', 'super_admin', 'underwriter']}>
                  <OAQuoteForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="/quote/oa/:id"
              element={
                <ProtectedRoute allowedRoles={['agent', 'producer', 'agent_admin', 'super_admin', 'underwriter']}>
                  <OAQuoteForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="/uw/oa-queue"
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'underwriter', 'accounting']}>
                  <OAQueue />
                </ProtectedRoute>
              }
            />
            <Route
              path="/policies/oa/:id"
              element={
                <ProtectedRoute allowedRoles={['agent', 'producer', 'agent_admin', 'super_admin', 'underwriter', 'accounting']}>
                  <OAPolicyDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/quote/resume/:token"
              element={
                <ProtectedRoute allowedRoles={['agent', 'producer', 'agent_admin', 'super_admin']}>
                  <QuoteResumeRedirect />
                </ProtectedRoute>
              }
            />
            <Route
              path="/quote/:id"
              element={
                <ProtectedRoute>
                  <QuoteForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="/quote/:id/edit"
              element={
                <ProtectedRoute allowedRoles={['agent', 'producer', 'agent_admin', 'super_admin']}>
                  <QuoteForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="/notifications"
              element={
                <ProtectedRoute>
                  <NotificationsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/declarations/:quoteId"
              element={
                <ProtectedRoute>
                  <DeclarationsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/payment/return"
              element={
                <ProtectedRoute>
                  <PaymentReturn />
                </ProtectedRoute>
              }
            />
            <Route
              path="/quote/:quoteId/compare"
              element={
                <ProtectedRoute>
                  <CompareCarriersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/payment/cancel"
              element={
                <ProtectedRoute>
                  <div className="min-h-screen flex items-center justify-center" data-testid="payment-cancel-page">
                    <div className="text-center max-w-md">
                      <p className="text-xl text-gray-800 font-semibold mb-2">Payment Cancelled</p>
                      <p className="text-sm text-gray-600 mb-4">No charges were made. You can retry payment anytime from your dashboard.</p>
                      <a href="/dashboard" className="text-indigo-600 underline">Back to Dashboard</a>
                    </div>
                  </div>
                </ProtectedRoute>
              }
            />

            {/* Catch all */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </Suspense>
        </BrowserRouter>
          </OverrideModeProvider>
        </AuthProvider>
      </BrandingProvider>
    </div>
  );
}

export default App;
